/**
 * AdjustCreditsModal
 *
 * Confirmation modal for manually crediting or debiting a user's credit balance.
 * Requires a free-text reason (min 20 chars) and a non-zero integer amount before
 * the Adjust button is enabled — guarding against accidental financial mutations.
 *
 * US-24-GR-07 — Manual Credit Ledger Adjustment
 *
 * Patterned after CancelJobModal (US-24-GR-06).
 */

import { useEffect, useRef, useState } from 'react';
import { type AdjustCreditsRequest } from '../api/orchestratorClient';

export interface AdjustCreditsModalProps {
  isOpen: boolean;
  profileId: string;
  profileDisplayName?: string;
  currentBalance?: number;
  onConfirm: (req: AdjustCreditsRequest) => Promise<void>;
  onCancel: () => void;
}

const MIN_REASON_LENGTH = 20;

/** Minimal spinner — animation gated on motion-safe (prefers-reduced-motion) */
function Spinner() {
  return (
    <span
      role="status"
      aria-label="Loading"
      className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full motion-safe:animate-spin"
    />
  );
}

function parseAmount(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '-') return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n === 0) return null;
  return n;
}

export function AdjustCreditsModal({
  isOpen,
  profileId,
  profileDisplayName,
  currentBalance,
  onConfirm,
  onCancel,
}: AdjustCreditsModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const [amountRaw, setAmountRaw] = useState('');
  const [reason, setReason] = useState('');
  const [referencedJobId, setReferencedJobId] = useState('');
  const [isInFlight, setIsInFlight] = useState(false);

  // Focus the Cancel (safe) button when modal opens; restore focus on close.
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
      const id = setTimeout(() => {
        cancelButtonRef.current?.focus();
      }, 0);
      return () => clearTimeout(id);
    } else {
      previouslyFocusedRef.current?.focus();
      previouslyFocusedRef.current = null;
    }
  }, [isOpen]);

  // ESC to cancel + focus trap
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (!isInFlight) onCancel();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        );

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isInFlight, onCancel]);

  if (!isOpen) return null;

  const parsedAmount = parseAmount(amountRaw);
  const isDebit = parsedAmount !== null && parsedAmount < 0;
  const isSubmitEnabled =
    parsedAmount !== null && reason.length >= MIN_REASON_LENGTH && !isInFlight;

  async function handleConfirm() {
    if (!isSubmitEnabled || parsedAmount === null) return;
    setIsInFlight(true);
    try {
      await onConfirm({
        profileId,
        amount: parsedAmount,
        reason,
        referencedJobId: referencedJobId.trim() || undefined,
      });
    } finally {
      setIsInFlight(false);
    }
  }

  const displayName = profileDisplayName ?? profileId;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        aria-hidden="true"
        onClick={() => { if (!isInFlight) onCancel(); }}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="adjust-credits-title"
          aria-describedby="adjust-credits-desc"
          className="w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl flex flex-col gap-4 p-6"
        >
          {/* Title */}
          <h2
            id="adjust-credits-title"
            className="text-sm font-semibold text-zinc-100"
          >
            Adjust credits for {displayName}
          </h2>

          <div id="adjust-credits-desc" className="flex flex-col gap-3">
            {/* Profile meta */}
            <div className="flex gap-2 flex-wrap items-center">
              <span className="text-xs bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded text-zinc-400 font-mono truncate max-w-[280px]">
                {profileId}
              </span>
              {currentBalance !== undefined && (
                <span className="text-xs bg-zinc-800 border border-zinc-700 px-2 py-0.5 rounded text-zinc-300">
                  Current balance:{' '}
                  <span className="font-medium text-zinc-100">
                    {currentBalance.toLocaleString()} credits
                  </span>
                </span>
              )}
            </div>

            {/* Warning callout */}
            <div className="rounded-lg bg-amber-950/40 border border-amber-500/30 px-3 py-2">
              <p className="text-xs text-amber-300">
                This writes to financial state and creates an audit-log entry. Action is irreversible.
              </p>
            </div>

            {/* Amount input */}
            <div>
              <label
                htmlFor="adjust-credits-amount"
                className="block text-xs font-medium text-zinc-400 mb-1"
              >
                Amount <span className="text-zinc-500">(integer, positive = credit/refund, negative = debit)</span>
              </label>
              <input
                id="adjust-credits-amount"
                type="number"
                step="1"
                value={amountRaw}
                onChange={(e) => setAmountRaw(e.target.value)}
                disabled={isInFlight}
                placeholder="e.g. 100 or -50"
                className="w-full rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-xs text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/70 disabled:opacity-50"
                aria-required="true"
              />
              {amountRaw.trim() !== '' && parsedAmount === null && (
                <p className="mt-1 text-xs text-red-400">
                  Must be a non-zero integer (e.g. 100 or -50).
                </p>
              )}
            </div>

            {/* Reason textarea */}
            <div>
              <label
                htmlFor="adjust-credits-reason"
                className="block text-xs font-medium text-zinc-400 mb-1"
              >
                Reason <span className="text-zinc-500">(required)</span>
              </label>
              <textarea
                id="adjust-credits-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={isInFlight}
                placeholder="Why are you adjusting this balance? (min 20 characters)"
                rows={4}
                className="w-full rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-xs text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/70 disabled:opacity-50 resize-none"
                aria-describedby="adjust-credits-char-count"
                aria-required="true"
              />
              <p
                id="adjust-credits-char-count"
                className={`mt-1 text-xs text-right ${
                  reason.length >= MIN_REASON_LENGTH
                    ? 'text-emerald-400'
                    : 'text-zinc-500'
                }`}
              >
                {reason.length} / {MIN_REASON_LENGTH} min
              </p>
            </div>

            {/* Optional referencedJobId */}
            <div>
              <label
                htmlFor="adjust-credits-job-id"
                className="block text-xs font-medium text-zinc-400 mb-1"
              >
                Referenced Job ID <span className="text-zinc-500">(optional, GUID)</span>
              </label>
              <input
                id="adjust-credits-job-id"
                type="text"
                value={referencedJobId}
                onChange={(e) => setReferencedJobId(e.target.value)}
                disabled={isInFlight}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="w-full rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-xs text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-violet-500/70 disabled:opacity-50 font-mono"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-1">
            {/* Cancel — default focus, safe action */}
            <button
              ref={cancelButtonRef}
              type="button"
              onClick={onCancel}
              disabled={isInFlight}
              aria-label="Cancel — do not adjust credits"
              className="min-h-[48px] px-4 py-2 rounded-lg border border-zinc-600 text-zinc-300 text-sm font-medium hover:bg-zinc-800 hover:text-zinc-100 disabled:opacity-40 transition-colors"
            >
              Cancel
            </button>

            {/* Adjust — destructive (red) if debit, primary (violet) if credit */}
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={!isSubmitEnabled}
              aria-label={`Adjust credits for ${displayName}`}
              aria-disabled={!isSubmitEnabled}
              className={[
                'min-h-[48px] px-5 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2 transition-colors',
                isDebit
                  ? 'bg-red-700 hover:bg-red-600 text-white'
                  : 'bg-violet-600 hover:bg-violet-500 text-zinc-950',
              ].join(' ')}
            >
              {isInFlight ? (
                <>
                  <Spinner />
                  Adjusting…
                </>
              ) : (
                'Adjust'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
